import { useState } from "react";
import { cn } from "@/lib/utils";
import type { DayPoint } from "@/api/workflow-insights";
import { formatDayLabel } from "@/lib/format";

/**
 * Stacked bar per day: completed (green) with failed (red) stacked on top.
 * No axis labels — the date range sits in the section header and hovering a
 * bar shows the exact numbers. The last two days render at full opacity so
 * the eye lands on "now".
 */
export function RunsPerDayChart({ points, loading = false }: { points: DayPoint[]; loading?: boolean }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...points.map((p) => p.completed + p.failed));
  const empty = !loading && points.every((p) => p.completed + p.failed === 0);

  return (
    <div className="relative">
      <div className="flex h-20 items-end gap-1 border-b border-border">
        {loading
          ? Array.from({ length: 30 }).map((_, i) => (
              <div key={i} className="flex-1 animate-pulse rounded-t-[2px] bg-surface-hover" style={{ height: `${20 + ((i * 37) % 60)}%` }} />
            ))
          : points.map((p, i) => {
              const total = p.completed + p.failed;
              const h = (total / max) * 100;
              const recent = i >= points.length - 2;
              return (
                <div
                  key={p.date}
                  className="relative flex h-full flex-1 flex-col justify-end"
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  aria-label={`${formatDayLabel(p.date)}: ${p.completed} completed, ${p.failed} failed`}
                >
                  {total > 0 ? (
                    <div className="flex flex-col justify-end transition-[height] duration-200" style={{ height: `${Math.max(h, 3)}%` }}>
                      {p.failed > 0 && (
                        <div
                          className={cn("w-full rounded-t-[2px] bg-[#dc2626] dark:bg-[#ef4444]", !recent && "opacity-80")}
                          style={{ height: `${(p.failed / total) * 100}%`, minHeight: 2 }}
                        />
                      )}
                      <div
                        className={cn("w-full bg-[#10a37f]", p.failed === 0 && "rounded-t-[2px]", !recent && "opacity-80")}
                        style={{ height: `${(p.completed / total) * 100}%`, minHeight: p.completed > 0 ? 2 : 0 }}
                      />
                    </div>
                  ) : (
                    <div className="h-px w-full bg-border/60" />
                  )}
                  {hover === i && (
                    <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border border-border bg-background px-2 py-1 text-[11px] text-text-secondary shadow-sm">
                      <span className="font-medium text-text-primary">{formatDayLabel(p.date)}</span>
                      {" · "}{p.completed} completed · {p.failed} failed
                    </div>
                  )}
                </div>
              );
            })}
      </div>
      {empty && (
        <p className="absolute inset-0 flex items-center justify-center text-[11.5px] text-text-muted">
          No runs in this period.
        </p>
      )}
    </div>
  );
}
